export const FCA_AD_FREQUENCY_MIN = 118
export const FCA_AD_FREQUENCY_MAX = 136.975

export interface FcaAdSuggestionValidationMessages {
  requiredIcao: string
  invalidIcao: string
  requiredFrequency: string
  invalidFrequency: string
  frequencyOutOfRange: string
}

export interface FcaAdSuggestionValidationResult {
  isValid: boolean
  adCode?: string
  frequency?: string
  errors: {
    adCode?: string
    frequency?: string
  }
}

const ICAO_PATTERN = /^[A-Z]{4}$/
const FREQUENCY_PATTERN = /^\d+(?:[.,]\d{1,3})?$/

export function normalizeFcaAdIcao(value: string): string {
  return value.trim().toUpperCase()
}

export function parseFcaAdFrequency(value: string): number | null {
  const normalized = value.trim().replace(',', '.')

  if (!FREQUENCY_PATTERN.test(normalized)) return null

  const frequency = Number(normalized)
  if (!Number.isFinite(frequency)) return null

  return frequency
}

export function normalizeFcaAdFrequency(value: string): string | null {
  const frequency = parseFcaAdFrequency(value)
  if (frequency === null) return null

  return frequency.toFixed(3)
}

export function validateFcaAdIcao(value: string, messages: FcaAdSuggestionValidationMessages): string | null {
  const normalized = normalizeFcaAdIcao(value ?? '')

  if (!normalized) return messages.requiredIcao
  if (!ICAO_PATTERN.test(normalized)) return messages.invalidIcao

  return null
}

export function validateFcaAdFrequency(value: string, messages: FcaAdSuggestionValidationMessages): string | null {
  const rawValue = value?.trim() ?? ''

  if (!rawValue) return messages.requiredFrequency

  const frequency = parseFcaAdFrequency(rawValue)
  if (frequency === null) return messages.invalidFrequency
  if (frequency < FCA_AD_FREQUENCY_MIN || frequency > FCA_AD_FREQUENCY_MAX) return messages.frequencyOutOfRange

  return null
}

export function validateFcaAdSuggestion(
  adCode: string,
  frequency: string,
  messages: FcaAdSuggestionValidationMessages
): FcaAdSuggestionValidationResult {
  const adCodeError = validateFcaAdIcao(adCode, messages)
  const frequencyError = validateFcaAdFrequency(frequency, messages)

  if (adCodeError || frequencyError) {
    return {
      isValid: false,
      errors: {
        adCode: adCodeError ?? undefined,
        frequency: frequencyError ?? undefined
      }
    }
  }

  return {
    isValid: true,
    adCode: normalizeFcaAdIcao(adCode),
    frequency: normalizeFcaAdFrequency(frequency),
    errors: {}
  }
}
